import { makeStyles } from 'tss-react/mui';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { ApiButton, Box, Grid, KeyValue, useSnackbarHandler } from 'web-library';
import { IconName } from 'web-library/stories/atom/Icon/Icon';
import IconWithLabel from 'web-library/stories/atom/IconWithLabel/IconWithLabel';
import { usePostGroupExamRegistrationIndividualPaymentLink } from '../../../../api-refactored/group-exam-registration/usePostGroupExamRegistrationIndividualPaymentLink';
import { ExamRegisteredSummaryResponse } from '../../../../api/candidate/exam/model';
import FormHeader from '../../../../components/form-items/FormHeader';
import PaymentStatusPill from '../../../../components/pills/PaymentStatusPill';
import { PaymentStatus } from '../../../../models/PaymentStatus';

import {typeTheme} from "web-library/theme/helper/tssHelper"
const useStyles = makeStyles()((theme) =>
({
  container: {
    paddingBottom: theme.spacing(3),
    maxWidth: 960,
  },
  sectionTitle: {
    paddingTop: theme.spacing(2),
    marginBottom: theme.spacing(3),
  },
  sendEmailButton: {
    color: theme.color.link.linkDefault01,
    padding: 0,
    minWidth: 0,
    '& span': {
       ...typeTheme(theme, 'buttonMedium'),
    },
  },
  paymentStatus:{
    [theme.breakpoints.up('md')]: {
      display: 'inline-flex',
    },
    [theme.breakpoints.down('md')]: {
      marginTop: theme.spacing(1),
    },
    '& span': {
      height: 'fit-content',
      alignSelf: 'center'
    },
  }
}));

interface IExamTransactions extends ExamRegisteredSummaryResponse {
  groupExamRegUuid?: string;
  candidateName: string;
}

const ExamTransactions = ({ ...props }: IExamTransactions) => {
  const { classes } = useStyles();
  const { t } = useTranslation();
  const { uuid } = useParams<any>();
  const { groupExamRegUuid } = props;
  const { transactionNo, paymentStatus, internalRef } = props.transactionDetails;
  const { paymentMode } = props.payment;
  const { snackbarHandler } = useSnackbarHandler();
  const postIndividualPaymentLink = usePostGroupExamRegistrationIndividualPaymentLink({
    onSuccess: (res, variables) => {
      if (groupExamRegUuid) {
        snackbarHandler?.setGlobalSnackbar({
          targetSeverity: 'success',
          targetData: t('exam->>RegistrationSummary.PaymentLinkSentSuccess', {
            candidateName: variables.callbackVariable.candidateName,
          }),
        });
      }
    },
    onError: (error, variables) => {
      if (groupExamRegUuid) {
        snackbarHandler?.setGlobalSnackbar({
          targetSeverity: 'error',
          targetData: t('exam->>RegistrationSummary.PaymentLinkSentError', {
            candidateName: variables.callbackVariable.candidateName,
          }),
          targetAnchorOrigin: {
            vertical: 'bottom',
            horizontal: 'left',
          },
        });
      }
    },
    onSettled: () => {
      postIndividualPaymentLink.reset();
    },
  });
  return (
    <Box className={classes.container}>
      <FormHeader title={t('appeal->>AppealSummary.TransactionDetails.TransactionDetails')} />
      <Grid container>
        <Grid xs={12} sm={6}>
          <KeyValue
            id="referenceNo"
            keyText={t('exam->>TransactionDetails.RefNo')}
            valueText={internalRef === '-' ? 'N/A' : internalRef || 'N/A'}
          />
        </Grid>
      </Grid>
      <Grid container>
        <Grid xs={12} sm={6}>
          <KeyValue
            id="transactionNo"
            keyText={t('appeal->>AppealSummary.TransactionDetails.TransactionNo')}
            valueText={transactionNo === '-' ? 'N/A' : transactionNo || 'N/A'}
          />
        </Grid>
      </Grid>
      <Grid container>
        <Grid xs={12} sm={6}>
          <KeyValue
            keyText={t('appeal->>AppealSummary.TransactionDetails.PaymentMethod')}
            valueText={paymentMode === '-' ? 'N/A' : paymentMode || 'N/A'}
          />
        </Grid>
        <Grid xs={12} sm={6}>
          <KeyValue
            keyText={t('appeal->>AppealSummary.TransactionDetails.PaymentStatus')}
            valueText={
              <Grid className={classes.paymentStatus}>
                <PaymentStatusPill paymentStatus={paymentStatus} />
                {paymentStatus === PaymentStatus.PENDING_PAYMENT && props.groupExamRegUuid && (
                  <ApiButton
                    variant="text"
                    color="primary"
                    onClick={() =>
                      postIndividualPaymentLink.mutate({
                        groupExamRegUuid: props.groupExamRegUuid || '',
                        individualExamRegUuid: uuid,
                        callbackVariable: {
                          candidateName: props.candidateName,
                        },
                      })
                    }
                    size="small"
                    status={postIndividualPaymentLink.status}
                    className={classes.sendEmailButton}
                  >
                    <IconWithLabel
                      iconName={IconName.email}
                      iconSize="M"
                      label="Send Payment Link"
                    />
                  </ApiButton>
                )}
              </Grid>
            }
          />
        </Grid>
      </Grid>
    </Box>
  );
};

export default ExamTransactions;
